/*  SeoPanel — Blog author "Full Ranking Machine" (iter100.157s)
    ────────────────────────────────────────────────────────────────
    Two rendering modes:
      • layout="sidebar" (default) — compact right-rail panel
      • layout="wide"              — full-width prominent stage
                                     (Score column · Fields · Checklist)

    Handles:
      • Live SEO score (0-100 + checklist) — polls /seo/score on debounce
      • One-click AI hydration via Gemini 3 Flash — /seo/generate
      • Focus keyword, meta title, meta description with live char counters
      • Tags chip input (3+ recommended)
      • Collapsible Open Graph section (og_title, og_description)
      • Collapsible JSON-LD schema preview
      • Actionable checklist derived from the deterministic rubric

    Contract with BlogAuthor:
      form     : { id, meta_title, meta_description, focus_keyword,
                   tags, og_title, og_description, seo_title, seo_description }
      update   : (patch) => void
      isEditable : bool — false for under_review / published (locked UI)
      onGeneratedRefresh : () => Promise<void>
                    — called after successful AI generation so parent can
                      pull the freshly-saved fields back into form state.
      layout   : "sidebar" | "wide"  (defaults to "sidebar")
*/
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Sparkle, CheckCircle, WarningCircle, CaretDown, CaretRight,
    Copy, Robot, X as XIcon, DeviceMobile, Monitor, DotsThree,
} from "@phosphor-icons/react";


// ── Panel shell (matches the pattern used in BlogAuthor). ──────────
function Panel({ title, right, children }) {
    return (
        <div className="rounded-2xl border border-border bg-white p-4">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-[13px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{title}</h3>
                {right}
            </div>
            {children}
        </div>
    );
}


// ── Score chip — colour by grade. ──────────────────────────────────
function ScoreChip({ score, grade, label, loading }) {
    const scheme = {
        excellent: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", dot: "bg-emerald-500" },
        good:      { bg: "bg-blue-50",    border: "border-blue-200",    text: "text-blue-800",    dot: "bg-blue-500" },
        fair:      { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-800",   dot: "bg-amber-500" },
        poor:      { bg: "bg-red-50",     border: "border-red-200",     text: "text-red-800",     dot: "bg-red-500" },
    }[grade] || { bg: "bg-slate-100", border: "border-slate-200", text: "text-slate-700", dot: "bg-slate-400" };

    if (loading) {
        return (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 border border-slate-200 text-slate-500 text-[12px]">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" />
                Scoring…
            </div>
        );
    }

    return (
        <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${scheme.bg} ${scheme.border} ${scheme.text}`}
            data-testid="seo-score-chip"
        >
            <span className={`w-1.5 h-1.5 rounded-full ${scheme.dot}`} />
            <span className="text-[13px] font-bold tabular-nums">{score}</span>
            <span className="text-[11px] font-medium opacity-80">/ 100</span>
            <span className="text-[11px] font-medium opacity-90 hidden sm:inline">· {label}</span>
        </div>
    );
}


// ── Char counter (turns amber near limit, red over). ───────────────
function CharCounter({ value, min, max }) {
    const len = (value || "").length;
    let cls = "text-slate-400";
    if (max && len > max) cls = "text-red-600 font-semibold";
    else if (max && len > max * 0.95) cls = "text-amber-600";
    else if (min && len > 0 && len < min) cls = "text-amber-600";
    return (
        <span className={`text-[11px] tabular-nums ${cls}`}>{len}{max ? ` / ${max}` : ""}</span>
    );
}


// ── Chip-style tag input. Enter or comma commits. Backspace deletes. ─
function TagsInput({ value, onChange, disabled }) {
    const [draft, setDraft] = useState("");
    const commit = () => {
        const t = draft.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
        if (!t) { setDraft(""); return; }
        if ((value || []).includes(t)) { setDraft(""); return; }
        onChange([...(value || []), t]);
        setDraft("");
    };
    const remove = (t) => onChange((value || []).filter((x) => x !== t));

    return (
        <div>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="seo-tags-chips">
                {(value || []).map((t) => (
                    <span
                        key={t}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-700 text-[11px] font-medium"
                        data-testid={`seo-tag-${t}`}
                    >
                        {t}
                        {!disabled && (
                            <button
                                type="button"
                                onClick={() => remove(t)}
                                className="hover:text-red-600"
                                aria-label={`Remove ${t}`}
                            >
                                <XIcon size={11} weight="bold" />
                            </button>
                        )}
                    </span>
                ))}
                {(value || []).length === 0 && (
                    <span className="text-[11px] text-muted-foreground italic">No tags yet — add 3+ for discovery.</span>
                )}
            </div>
            {!disabled && (
                <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); }
                        else if (e.key === "Backspace" && !draft && (value || []).length) { remove(value[value.length - 1]); }
                    }}
                    onBlur={commit}
                    placeholder="Type a tag and press Enter"
                    className="h-8 text-sm"
                    data-testid="seo-tags-input"
                />
            )}
        </div>
    );
}


// ── Small collapsible section. ─────────────────────────────────────
function Section({ label, defaultOpen = false, children }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="border-t border-border pt-3 mt-3">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 text-[12px] font-semibold text-foreground hover:text-primary"
            >
                {open ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
                {label}
            </button>
            {open && <div className="mt-3 space-y-2.5">{children}</div>}
        </div>
    );
}


// ── Checklist row. ─────────────────────────────────────────────────
function ChecklistRow({ check }) {
    return (
        <div className="flex items-start gap-2 text-[12px]" data-testid={`seo-check-${check.id}`}>
            {check.passed
                ? <CheckCircle size={13} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
                : <WarningCircle size={13} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
            }
            <div className="min-w-0">
                <div className={`${check.passed ? "text-slate-600" : "text-slate-800 font-medium"} leading-snug`}>
                    {check.label}
                </div>
                {!check.passed && check.hint && (
                    <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{check.hint}</div>
                )}
            </div>
        </div>
    );
}


// ══════════════════════════════════════════════════════════════════
//   SERP Preview — mock Google search result (desktop + mobile)
//   ═════════════════════════════════════════════════════════════════
//   Shows exactly how the meta title + URL + description will render
//   on Google. Live-updates as the author types. Truncation mirrors
//   real Google behaviour (pixel-ish caps: title ~60, desc ~155).
// ══════════════════════════════════════════════════════════════════
function SerpPreview({ form }) {
    const [mode, setMode] = useState("desktop");

    // Base URL — mirrors _public_base_url() in blog_seo.py.
    const base = (() => {
        const raw =
            typeof window !== "undefined"
                ? (process.env.REACT_APP_PUBLIC_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin)
                : "";
        try {
            return new URL(raw).hostname.replace(/^www\./, "");
        } catch {
            return "invisique.com";
        }
    })();

    const slug = (form.slug || "").trim() || "your-post-slug";
    const title = (form.meta_title || form.title || "").trim() || "Your article title — becomes the blue Google link";
    const description = (
        form.meta_description ||
        form.excerpt ||
        "Your meta description will appear here — write a benefit-led 120–160 character snippet that earns the click from search results."
    ).trim();

    // Google usually shows a "1 hour ago" style date next to the description.
    const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    // Rough truncation — Google truncates by pixel width not char count,
    // but 60 / 155 is the accepted rule of thumb.
    const titleCapped = mode === "desktop" ? 60 : 78;
    const descCapped = mode === "desktop" ? 158 : 130;
    const truncatedTitle = title.length > titleCapped ? title.slice(0, titleCapped - 1) + "…" : title;
    const truncatedDesc = description.length > descCapped ? description.slice(0, descCapped - 1) + "…" : description;

    return (
        <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="seo-serp-preview">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-slate-50/60">
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                        Google search preview
                    </span>
                    <span className="text-[10px] text-slate-400">·</span>
                    <span className="text-[10px] text-slate-500">live</span>
                </div>
                <div className="inline-flex bg-slate-100 rounded-full p-0.5 border border-slate-200" data-testid="seo-serp-mode-toggle">
                    <button
                        type="button"
                        onClick={() => setMode("desktop")}
                        className={`h-6 px-2.5 rounded-full text-[10px] font-semibold inline-flex items-center gap-1 transition-colors ${
                            mode === "desktop" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                        }`}
                        data-testid="seo-serp-mode-desktop"
                    >
                        <Monitor size={11} weight="bold" />
                        Desktop
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode("mobile")}
                        className={`h-6 px-2.5 rounded-full text-[10px] font-semibold inline-flex items-center gap-1 transition-colors ${
                            mode === "mobile" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                        }`}
                        data-testid="seo-serp-mode-mobile"
                    >
                        <DeviceMobile size={11} weight="bold" />
                        Mobile
                    </button>
                </div>
            </div>

            {mode === "desktop" ? (
                <div className="px-6 py-4">
                    <div className="max-w-[640px] font-sans">
                        {/* Favicon + breadcrumb row */}
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-6 h-6 rounded-full bg-slate-900 grid place-items-center text-white text-[10px] font-bold shadow-sm">
                                I
                            </div>
                            <div className="min-w-0">
                                <div className="text-[13px] leading-tight text-slate-800 truncate">{base}</div>
                                <div className="text-[12px] leading-tight text-slate-500 truncate">
                                    https://{base} <span className="text-slate-400"> › blog › {slug}</span>
                                </div>
                            </div>
                        </div>
                        {/* Title */}
                        <div className="mt-1">
                            <span className="text-[20px] leading-[1.3] font-normal text-[#1a0dab] hover:underline cursor-pointer">
                                {truncatedTitle}
                            </span>
                        </div>
                        {/* Description */}
                        <p className="mt-1 text-[14px] leading-[1.58] text-[#4d5156]">
                            <span className="text-slate-500">{today} — </span>
                            {truncatedDesc}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="px-4 py-4 bg-slate-50/30">
                    <div className="mx-auto max-w-[360px] rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-5 h-5 rounded-full bg-slate-900 grid place-items-center text-white text-[9px] font-bold">
                                I
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-[12px] leading-tight text-slate-800 truncate">{base}</div>
                                <div className="text-[11px] leading-tight text-slate-500 truncate">
                                    › blog › {slug}
                                </div>
                            </div>
                            <DotsThree size={16} weight="bold" className="text-slate-400" />
                        </div>
                        <div className="mt-1">
                            <span className="text-[17px] leading-[1.3] font-normal text-[#1a0dab]">
                                {truncatedTitle}
                            </span>
                        </div>
                        <p className="mt-1 text-[13px] leading-[1.55] text-[#4d5156]">
                            {truncatedDesc}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}


// ══════════════════════════════════════════════════════════════════
//   Main panel
// ══════════════════════════════════════════════════════════════════
export default function SeoPanel({ form, update, isEditable, onGeneratedRefresh, layout = "sidebar" }) {
    const [score, setScore] = useState(null);
    const [scoreLoading, setScoreLoading] = useState(false);
    const [jsonLd, setJsonLd] = useState(null);
    const [generating, setGenerating] = useState(false);
    const debounceRef = useRef(null);

    // Debounced live score fetch — 800ms after any relevant field change.
    const relevantChangeKey = [
        form.id, form.title, form.meta_title, form.meta_description,
        form.focus_keyword, (form.tags || []).join(","), form.content_md,
        form.hero_image_url, form.hero_image_alt, form.slug,
    ].join("|");

    const fetchScore = useCallback(async () => {
        if (!form.id) { setScore(null); setJsonLd(null); return; }
        setScoreLoading(true);
        try {
            const { data } = await api.get(`/blog-builder/posts/${form.id}/seo/score`);
            setScore(data.score);
            setJsonLd(data.json_ld);
        } catch {
            // silent — score is a nice-to-have.
        } finally {
            setScoreLoading(false);
        }
    }, [form.id]);

    useEffect(() => {
        if (!form.id) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(fetchScore, 800);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [relevantChangeKey, fetchScore, form.id]);

    // First mount — instant score.
    useEffect(() => {
        if (form.id) fetchScore();
    }, [form.id, fetchScore]);

    const handleGenerate = async () => {
        if (!form.id) {
            toast.error("Save the draft first so the AI has something to read.");
            return;
        }
        if (!form.title.trim() || !(form.content_md || "").trim()) {
            toast.error("Add a title and some body content before generating SEO.");
            return;
        }
        setGenerating(true);
        try {
            const { data } = await api.post(`/blog-builder/posts/${form.id}/seo/generate`);
            const g = data.generated || {};
            update({
                meta_title: g.meta_title || "",
                meta_description: g.meta_description || "",
                focus_keyword: g.focus_keyword || "",
                tags: g.tags || [],
                og_title: g.og_title || g.meta_title || "",
                og_description: g.og_description || g.meta_description || "",
                // Keep legacy fields in lock-step so the rest of the app keeps working.
                seo_title: g.meta_title || form.seo_title,
                seo_description: g.meta_description || form.seo_description,
            });
            setScore(data.score);
            setJsonLd(data.json_ld);
            toast.success("SEO metadata generated by Gemini.");
            onGeneratedRefresh?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't generate SEO right now.");
        } finally {
            setGenerating(false);
        }
    };

    const copyJsonLd = () => {
        if (!jsonLd) return;
        navigator.clipboard.writeText(JSON.stringify(jsonLd, null, 2));
        toast.success("JSON-LD copied — paste into your HTML head.");
    };

    // ── Reusable sub-parts (shared by both layouts) ────────────────
    const generateButton = (
        <>
            <Button
                type="button"
                onClick={handleGenerate}
                disabled={!isEditable || generating || !form.id}
                className="w-full h-9 font-semibold bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="seo-generate-btn"
            >
                {generating ? (
                    <>
                        <Robot size={13} weight="fill" className="mr-1.5 animate-pulse" />
                        Analysing with Gemini…
                    </>
                ) : (
                    <>
                        <Sparkle size={13} weight="fill" className="mr-1.5" />
                        Generate SEO with AI
                    </>
                )}
            </Button>
            {!form.id && (
                <p className="text-[11px] text-muted-foreground mt-1.5 leading-snug">
                    Save your draft to unlock AI generation.
                </p>
            )}
        </>
    );

    const focusKeywordField = (
        <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-muted-foreground">Focus keyword</label>
            <Input
                value={form.focus_keyword || ""}
                onChange={(e) => update({ focus_keyword: e.target.value })}
                placeholder="e.g. clear aligner therapy"
                disabled={!isEditable}
                className="h-9 text-sm disabled:opacity-70"
                data-testid="seo-focus-keyword-input"
            />
            <p className="text-[11px] text-muted-foreground">The primary phrase you want to rank for.</p>
        </div>
    );

    const metaTitleField = (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between">
                <label className="text-[11px] font-semibold text-muted-foreground">Meta title</label>
                <CharCounter value={form.meta_title} min={30} max={60} />
            </div>
            <Input
                value={form.meta_title || ""}
                onChange={(e) => update({
                    meta_title: e.target.value,
                    seo_title: e.target.value,
                })}
                placeholder="Compelling, front-loaded (30-60 chars)"
                disabled={!isEditable}
                className="h-9 text-sm disabled:opacity-70"
                data-testid="seo-meta-title-input"
            />
        </div>
    );

    const metaDescriptionField = (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between">
                <label className="text-[11px] font-semibold text-muted-foreground">Meta description</label>
                <CharCounter value={form.meta_description} min={120} max={160} />
            </div>
            <Textarea
                value={form.meta_description || ""}
                onChange={(e) => update({
                    meta_description: e.target.value,
                    seo_description: e.target.value,
                })}
                placeholder="Benefit-led snippet that earns the click (120-160 chars)"
                rows={3}
                disabled={!isEditable}
                className="text-sm disabled:opacity-70"
                data-testid="seo-meta-description-input"
            />
        </div>
    );

    const tagsField = (
        <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-muted-foreground">Tags</label>
            <TagsInput
                value={form.tags}
                onChange={(tags) => update({ tags })}
                disabled={!isEditable}
            />
        </div>
    );

    const openGraphSection = (
        <>
            <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                    <label className="text-[11px] font-semibold text-muted-foreground">OG title</label>
                    <CharCounter value={form.og_title} max={90} />
                </div>
                <Input
                    value={form.og_title || ""}
                    onChange={(e) => update({ og_title: e.target.value })}
                    placeholder="Punchier headline for social shares"
                    disabled={!isEditable}
                    className="h-9 text-sm disabled:opacity-70"
                    data-testid="seo-og-title-input"
                />
            </div>
            <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                    <label className="text-[11px] font-semibold text-muted-foreground">OG description</label>
                    <CharCounter value={form.og_description} max={250} />
                </div>
                <Textarea
                    value={form.og_description || ""}
                    onChange={(e) => update({ og_description: e.target.value })}
                    placeholder="What people see when they share the link"
                    rows={2}
                    disabled={!isEditable}
                    className="text-sm disabled:opacity-70"
                    data-testid="seo-og-description-input"
                />
            </div>
        </>
    );

    const jsonLdSection = jsonLd ? (
        <>
            <div className="rounded-md bg-slate-950 text-emerald-300 font-mono text-[10.5px] leading-snug p-3 overflow-auto max-h-64" data-testid="seo-jsonld-preview">
                <pre className="whitespace-pre-wrap break-all">{JSON.stringify(jsonLd, null, 2)}</pre>
            </div>
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={copyJsonLd}
                className="h-8 text-[11px]"
                data-testid="seo-jsonld-copy-btn"
            >
                <Copy size={11} weight="bold" className="mr-1" />
                Copy JSON-LD
            </Button>
        </>
    ) : (
        <p className="text-[11px] text-muted-foreground">Saves your BlogPosting schema for search engines. Populates once the post has a slug.</p>
    );

    const checklistBlock = score?.checks && (
        <div className="space-y-2" data-testid="seo-checklist">
            {score.checks.map((c) => <ChecklistRow key={c.id} check={c} />)}
        </div>
    );

    const readabilityBlock = (typeof score?.readability_grade === "number" && score.readability_grade > 0) && (
        <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center justify-between text-[11px]">
                <span className="text-muted-foreground">Readability (Flesch–Kincaid)</span>
                <span className={`font-semibold tabular-nums ${score.readability_grade > 12 ? "text-amber-700" : "text-emerald-700"}`}>
                    Grade {score.readability_grade}
                </span>
            </div>
            <div className="flex items-center justify-between text-[11px] mt-1">
                <span className="text-muted-foreground">Word count</span>
                <span className="font-semibold tabular-nums text-slate-700">{score.word_count}</span>
            </div>
        </div>
    );

    // ── Wide (full-width) layout ───────────────────────────────────
    if (layout === "wide") {
        return (
            <WideLayout
                score={score}
                scoreLoading={scoreLoading || (!score && form.id)}
                generateButton={generateButton}
                focusKeywordField={focusKeywordField}
                metaTitleField={metaTitleField}
                metaDescriptionField={metaDescriptionField}
                tagsField={tagsField}
                openGraphSection={openGraphSection}
                jsonLdSection={jsonLdSection}
                checklistBlock={checklistBlock}
                readabilityBlock={readabilityBlock}
                hasPostId={Boolean(form.id)}
                form={form}
            />
        );
    }

    // ── Sidebar (default) layout ───────────────────────────────────
    return (
        <Panel
            title="SEO — Ranking Machine"
            right={
                <ScoreChip
                    score={score?.score ?? 0}
                    grade={score?.grade}
                    label={score?.label}
                    loading={scoreLoading || (!score && form.id)}
                />
            }
        >
            {generateButton}

            <div className="mt-4">{focusKeywordField}</div>
            <div className="mt-4">{metaTitleField}</div>
            <div className="mt-4">{metaDescriptionField}</div>
            <div className="mt-4">{tagsField}</div>

            <Section label="Open Graph (Facebook, LinkedIn, Twitter)">
                {openGraphSection}
            </Section>

            <Section label="Structured data (JSON-LD)">
                {jsonLdSection}
            </Section>

            {score?.checks && (
                <Section label={`Checklist (${score.checks.filter((c) => c.passed).length} / ${score.checks.length})`} defaultOpen>
                    {checklistBlock}
                    {readabilityBlock}
                </Section>
            )}
        </Panel>
    );
}


// ══════════════════════════════════════════════════════════════════
//   Wide layout — full-width prominent stage
//   ═════════════════════════════════════════════════════════════════
//   Left column  : Big score dial + Generate CTA
//   Mid column   : Focus keyword + Meta title + Meta description + Tags
//   Right column : Live checklist (always visible) + readability
//   Bottom row   : Collapsible Open Graph + JSON-LD (side by side)
// ══════════════════════════════════════════════════════════════════
function WideLayout({
    score, scoreLoading, generateButton,
    focusKeywordField, metaTitleField, metaDescriptionField, tagsField,
    openGraphSection, jsonLdSection, checklistBlock, readabilityBlock,
    hasPostId, form,
}) {
    const passed = score?.checks?.filter((c) => c.passed).length || 0;
    const total = score?.checks?.length || 0;

    return (
        <div
            className="rounded-3xl border border-border bg-gradient-to-br from-white via-slate-50/70 to-white shadow-[0_2px_20px_-8px_rgba(15,23,42,0.08)] overflow-hidden"
            data-testid="seo-panel-wide"
        >
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-border bg-white/60 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center shadow-inner">
                        <Sparkle size={16} weight="fill" />
                    </div>
                    <div>
                        <div className="font-display font-bold text-base tracking-tight">SEO — Ranking Machine</div>
                        <div className="text-[11px] text-muted-foreground">Auto-generated by Gemini · Live scoring · One-click optimisation</div>
                    </div>
                </div>
                <div className="hidden sm:block">
                    <ScoreChip
                        score={score?.score ?? 0}
                        grade={score?.grade}
                        label={score?.label}
                        loading={scoreLoading}
                    />
                </div>
            </div>

            {/* SERP live preview — full width */}
            <div className="px-6 pt-5">
                <SerpPreview form={form} />
            </div>

            {/* Body — 3 columns on lg+, stack on smaller */}
            <div className="grid lg:grid-cols-[220px_minmax(0,1fr)_320px] gap-6 p-6">
                {/* Column 1 — Big score + Generate */}
                <div className="flex flex-col items-center lg:items-stretch gap-4">
                    <BigScoreDial score={score?.score} grade={score?.grade} label={score?.label} loading={scoreLoading} />
                    <div className="w-full">
                        {generateButton}
                    </div>
                </div>

                {/* Column 2 — Fields */}
                <div className="space-y-4 min-w-0">
                    {focusKeywordField}
                    {metaTitleField}
                    {metaDescriptionField}
                    {tagsField}
                </div>

                {/* Column 3 — Checklist */}
                <div className="rounded-2xl border border-border bg-white p-4 self-start">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                            Checklist
                        </div>
                        {total > 0 && (
                            <span className="text-[11px] font-semibold text-slate-600 tabular-nums">
                                {passed} / {total}
                            </span>
                        )}
                    </div>
                    {checklistBlock || (
                        <p className="text-[11px] text-muted-foreground leading-snug">
                            {hasPostId
                                ? "Analysing your post…"
                                : "Save the draft to unlock live SEO scoring."}
                        </p>
                    )}
                    {readabilityBlock}
                </div>
            </div>

            {/* Collapsible bottom row — OG + JSON-LD */}
            <div className="grid md:grid-cols-2 gap-4 px-6 pb-6">
                <div className="rounded-2xl border border-border bg-white p-4">
                    <Section label="Open Graph (Facebook, LinkedIn, Twitter)">
                        {openGraphSection}
                    </Section>
                </div>
                <div className="rounded-2xl border border-border bg-white p-4">
                    <Section label="Structured data (JSON-LD)">
                        {jsonLdSection}
                    </Section>
                </div>
            </div>
        </div>
    );
}


// ── Big circular score dial for the wide layout. ────────────────────
function BigScoreDial({ score, grade, label, loading }) {
    const scheme = {
        excellent: { ring: "stroke-emerald-500", text: "text-emerald-700", tone: "text-emerald-600" },
        good:      { ring: "stroke-blue-500",    text: "text-blue-700",    tone: "text-blue-600" },
        fair:      { ring: "stroke-amber-500",   text: "text-amber-700",   tone: "text-amber-600" },
        poor:      { ring: "stroke-red-500",     text: "text-red-700",     tone: "text-red-600" },
    }[grade] || { ring: "stroke-slate-400", text: "text-slate-700", tone: "text-slate-500" };

    const s = Math.max(0, Math.min(100, Number(score) || 0));
    const R = 42;
    const C = 2 * Math.PI * R;
    const offset = C - (s / 100) * C;

    return (
        <div className="flex flex-col items-center gap-2" data-testid="seo-score-dial">
            <div className="relative w-[132px] h-[132px]">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    <circle
                        cx="50" cy="50" r={R}
                        fill="none" strokeWidth="7"
                        className="stroke-slate-200"
                    />
                    <circle
                        cx="50" cy="50" r={R}
                        fill="none" strokeWidth="7" strokeLinecap="round"
                        className={`${scheme.ring} transition-all duration-700 ease-out ${loading ? "opacity-40" : "opacity-100"}`}
                        strokeDasharray={C}
                        strokeDashoffset={loading ? C : offset}
                    />
                </svg>
                <div className="absolute inset-0 grid place-items-center">
                    <div className="text-center">
                        <div className={`text-4xl font-display font-bold tabular-nums leading-none ${scheme.text}`}>
                            {loading ? "…" : s}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                            / 100
                        </div>
                    </div>
                </div>
            </div>
            <div className={`text-[11px] font-semibold uppercase tracking-wider ${scheme.tone} text-center leading-tight`}>
                {loading ? "Scoring…" : (label || "Not scored yet")}
            </div>
        </div>
    );
}
